import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Snackbar from "@mui/material/Snackbar";
import Typography from "@mui/material/Typography";
import { AddIcon } from "../../shared/components/icons";
import { TreeBrowser } from "./TreeBrowser";
import { ContainerDetails } from "./ContainerDetails";
import { ContainerMenu } from "./ContainerMenu";
import { BulkMoveBar, BULK_MOVE_BAR_SPACER, BULK_MOVE_SNACKBAR_OFFSET } from "../../shared/components/BulkMoveBar";
import { BulkMoveDialog, type BulkMoveOutcome } from "../catalogs/BulkMoveDialog";
import { useShell } from "../shell/useShell";
import {
  selectAllBulkSelection,
  toggleBulkSelection,
  type DeadlineFilter,
} from "@egalregal/shared";
import { useAppStore } from "../../shared/store/app";
import { usePreferencesStore } from "../../shared/store/preferences";
import { useT } from "../../shared/i18n";
import { ItemList } from "../items/ItemList";
import { ScanToAddDialog } from "../items/ScanToAddDialog";
import { useDemoMode } from "../demo/useDemoMode";

/**
 * Browse by location: one merged container page (container-view 01, variant B).
 * The containers row (`Containers · N`, `More ▾`, `Edit`), the sub-container
 * list, the container's details and its items are one scroll. Every row drills
 * into `/browse/:id`; there is no separate detail step for leaves.
 *
 * The shell's content header owns back, title, kind chip, crumbs and the
 * identity line. `More → Hide details` folds the container's own content away
 * so the level can be read alone; the items block stays in place.
 */
export function BrowseView() {
  const t = useT();
  const demoMode = useDemoMode();
  const navigate = useNavigate();
  const containers = useAppStore((s) => s.containers);
  const selectedEntryId = useAppStore((s) => s.selectedEntryId);
  const selectEntry = useAppStore((s) => s.selectEntry);
  const openContainer = useAppStore((s) => s.openContainer);
  const { containerId } = useParams<{ containerId?: string }>();
  const currentId = containerId ?? null;
  // One preference, one behaviour across the item and container lists.
  const deadlineView = usePreferencesStore((s) => s.itemDeadlineView);
  const setDeadlineView = usePreferencesStore((s) => s.setItemDeadlineView);
  const shell = useShell();
  const [deadlineSort, setDeadlineSort] = useState<"alpha" | "soonest">("alpha");
  // Client-side threshold filter shared with the per-container item list.
  const [deadlineFilters, setDeadlineFilters] = useState<DeadlineFilter[]>([]);
  // Details are open by default; `More → Hide details` folds them.
  const [detailsOpen, setDetailsOpen] = useState(true);
  // Bulk mode is local selection state (bulk-move 05): leaving the level or
  // switching entries drops it, and it never survives a full success.
  const [bulkMode, setBulkMode] = useState(false);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [moveOpen, setMoveOpen] = useState(false);
  const [notice, setNotice] = useState<BulkNotice | null>(null);
  const [scanToAddOpen, setScanToAddOpen] = useState(false);
  const [itemRefreshKey, setItemRefreshKey] = useState(0);
  // A bulk-move link switches the catalog entry while the route still points at
  // the source container. Suppress the deep-link resolver until the switch
  // lands, or it re-selects the source entry from the stale route id. This is
  // state, not a ref, so the resolver re-runs if the destination load fails.
  const [switchingEntry, setSwitchingEntry] = useState(false);

  useEffect(() => {
    setBulkMode(false);
    setSelected(new Set());
    setMoveOpen(false);
    // The details block is a per-container view, not a global preference: a new
    // level always starts with its details visible.
    setDetailsOpen(true);
  }, [currentId, selectedEntryId]);

  const current = currentId ? containers.find((c) => c.id === currentId) : undefined;

  // A deep link (including an old `/detail/:id` bookmark redirected here) may
  // name a container outside the selected catalog entry, or one already
  // deleted. Resolve it once through the world-scoped opener; only a confirmed
  // `missing` result falls back to the level. A transient lookup failure keeps
  // the loading state — the "might be offline" case (catalog-entry-sharing 11).
  useEffect(() => {
    if (!currentId || current || switchingEntry) return;
    let cancelled = false;
    void openContainer(currentId).then((result) => {
      if (cancelled) return;
      if (result === "missing") navigate("/browse", { replace: true });
    });
    return () => {
      cancelled = true;
    };
  }, [currentId, current, openContainer, navigate, switchingEntry]);

  const handleDeadlineViewChange = (enabled: boolean) => {
    setDeadlineView(enabled);
    // Turning the view off returns the rows to tree order; a soonest sort and
    // the filters have no header to live in any more.
    if (!enabled) {
      setDeadlineSort("alpha");
      setDeadlineFilters([]);
    }
  };
  const toggleDeadlineSort = () =>
    setDeadlineSort((sort) => (sort === "soonest" ? "alpha" : "soonest"));

  const toggleBulkMode = () => {
    setBulkMode((active) => !active);
    setSelected(new Set());
  };

  /** Row click: toggles membership, refusing to grow past the product cap. */
  const toggleSelection = (id: string) =>
    setSelected((current) => toggleBulkSelection(current, id));

  /** Select-all takes the visible rows in order and stops at the 50-row cap. */
  const selectAll = (ids: string[]) =>
    setSelected((current) => selectAllBulkSelection(ids, current));

  const completeMove = (outcome: BulkMoveOutcome) => {
    setMoveOpen(false);
    if (outcome.result.failed.length > 0) {
      // Partial commit: the moved rows left the source slice; the failed rows
      // stay selected so the user can retry without starting over.
      setSelected(new Set(outcome.result.failed.map((failure) => failure.id)));
      const reasons = [...new Set(outcome.result.failed.map((failure) => failure.message))].join(" · ");
      const droppedSuffix =
        outcome.result.droppedPlacements > 0
          ? ` ${t("bulkMovePartialDropped", {
              dropped: String(outcome.result.droppedPlacements),
            })}`
          : "";
      setNotice({
        severity: "warning",
        message:
          t("bulkMovePartial", {
            moved: String(outcome.result.moved.length),
            total: String(outcome.result.moved.length + outcome.result.failed.length),
            failed: String(outcome.result.failed.length),
            reasons,
          }) + droppedSuffix,
      });
      return;
    }
    setBulkMode(false);
    setSelected(new Set());
    setNotice({
      severity: "success",
      message:
        outcome.result.droppedPlacements > 0
          ? t("bulkMoveSuccessDropped", {
              count: String(outcome.result.moved.length),
              destination: outcome.destinationLabel,
              dropped: String(outcome.result.droppedPlacements),
            })
          : t("bulkMoveSuccess", {
              count: String(outcome.result.moved.length),
              destination: outcome.destinationLabel,
            }),
      open: {
        entryId: outcome.entryId,
        path: outcome.parentId ? `/browse/${outcome.parentId}` : "/browse",
      },
    });
  };

  /** The snackbar link scopes the destination entry before navigating to it. */
  const openDestination = async () => {
    const target = notice?.open;
    if (!target) return;
    setNotice(null);
    setSwitchingEntry(true);
    try {
      // Navigate first: selectEntry clears the container slice, and the old
      // /browse/:id route would otherwise re-open the source container (and
      // re-select its entry) before the destination entry lands.
      navigate(target.path);
      if (target.entryId !== selectedEntryId) await selectEntry(target.entryId);
    } finally {
      setSwitchingEntry(false);
    }
  };

  // The container exists but its catalog entry may still be loading. Do not
  // paint the top level in its place: that would show a wrong breadcrumb and a
  // tree the container is not in.
  if (currentId && !current) {
    return (
      <Typography variant="body1" color="text.secondary">
        {t("loading")}
      </Typography>
    );
  }

  return (
    <Box data-testid="browse-view">
      <TreeBrowser
        containers={containers}
        currentId={currentId}
        showBreadcrumbs={false}
        headerActions={
          <ContainerMenu
            current={current}
            deadlineView={deadlineView}
            onToggleDeadlineView={handleDeadlineViewChange}
            deadlineFilters={deadlineFilters}
            onDeadlineFilters={setDeadlineFilters}
            detailsOpen={detailsOpen}
            onToggleDetails={() => setDetailsOpen((open) => !open)}
            onOpenDetails={() => setDetailsOpen(true)}
            bulkMode={bulkMode}
            canSelect={!demoMode && selectedEntryId !== undefined}
            onToggleBulkMode={toggleBulkMode}
          />
        }
        deadlineView={deadlineView}
        deadlineSort={deadlineSort}
        onToggleDeadlineSort={toggleDeadlineSort}
        deadlineFilters={deadlineFilters}
        bulkMode={bulkMode}
        selectedIds={selected}
        onToggleSelect={toggleSelection}
        onSelectAll={selectAll}
        testIdPrefix={bulkMode ? "browse-row" : undefined}
      />

      {/* Phone has no nav Add: the Browse root carries the dashed row (spec §2). */}
      {currentId === null && shell === "phone" && !demoMode && (
        <Button
          fullWidth
          variant="outlined"
          startIcon={<AddIcon fontSize="small" />}
          onClick={() => navigate("/add")}
          data-testid="browse-add-row"
          sx={{ mt: 2, minHeight: 56, borderStyle: "dashed" }}
        >
          {t("addTitle")}
        </Button>
      )}

      {current && detailsOpen && <ContainerDetails container={current} />}

      {current && (
        <Box data-testid="container-items">
          <ItemList
            containerId={current.id}
            onScanRequest={() => setScanToAddOpen(true)}
            refreshKey={itemRefreshKey}
          />
        </Box>
      )}

      {current && !demoMode && (
        <ScanToAddDialog
          open={scanToAddOpen}
          onClose={() => setScanToAddOpen(false)}
          containerId={current.id}
          onItemAdded={() => setItemRefreshKey((key) => key + 1)}
        />
      )}

      {/* Let the last rows scroll clear of the fixed bar. */}
      {bulkMode && !demoMode && <Box sx={{ height: BULK_MOVE_BAR_SPACER }} aria-hidden />}

      {bulkMode && !demoMode && !moveOpen && (
        <BulkMoveBar count={selected.size} onMove={() => setMoveOpen(true)} />
      )}

      {selectedEntryId !== undefined && !demoMode && (
        <BulkMoveDialog
          open={moveOpen}
          sourceEntryId={selectedEntryId}
          currentContainerId={currentId}
          selectedIds={[...selected]}
          onClose={() => setMoveOpen(false)}
          onComplete={completeMove}
        />
      )}

      <Snackbar
        open={notice !== null}
        autoHideDuration={6000}
        onClose={() => setNotice(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        sx={{ bottom: bulkMode ? BULK_MOVE_SNACKBAR_OFFSET : undefined }}
      >
        <Alert
          severity={notice?.severity ?? "success"}
          onClose={() => setNotice(null)}
          action={
            notice?.open ? (
              <Button color="inherit" size="small" onClick={() => void openDestination()} data-testid="bulk-move-open">
                {t("open")}
              </Button>
            ) : undefined
          }
        >
          <span data-testid="bulk-move-notice">{notice?.message}</span>
        </Alert>
      </Snackbar>
    </Box>
  );
}

/** A move outcome surfaced in the Browse snackbar; `open` links to the destination. */
interface BulkNotice {
  severity: "success" | "warning";
  message: string;
  open?: { entryId: string; path: string };
}
